import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { RxHamburgerMenu } from 'react-icons/rx';
import { IoCloseOutline } from 'react-icons/io5';
import LogoImg from '../assets/images/logo-white.svg';
import { setShowMoreTag } from '../redux/reducers/commonSlice';
import AutoLogout from './autoLogOut';
import { performLogout } from '../utils/logout';
import './AdminHeader.css';

const toTitleCase = (str = '') =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

function AdminHeader({ title, setMediaListFlag }) {
    const { isAuthenticated } = useSelector((state) => state.authentication);
    const { userData } = useSelector((state) => state.masterData);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const dropdownRef = useRef(null);

    const role = userData?.roleDisplayName;

    const logout = () => { performLogout(dispatch); };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const goMediaList = () => {
        if ((role === 'Super_admin' || role === 'APPROVER') && setMediaListFlag) {
            setMediaListFlag(true);
        }
        setIsOpen(false);
        setMobileOpen(false);
        navigate('/media-list');
    };

    const goHome = () => {
        dispatch(setShowMoreTag(false));
        navigate('/');
    };

    return (
        <header className="admin-header">
            <AutoLogout isAuthenticated={isAuthenticated} onLogout={logout} />
            <div className="admin-header-inner">
                <span
                    className="admin-header-brand"
                    onClick={goHome}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') goHome(); }}
                >
                    <img src={LogoImg} alt="Visit Qatar" />
                </span>
                {title && <h1 className="admin-header-title">{title}</h1>}

                <button
                    className="admin-header-mobile-toggle"
                    type="button"
                    aria-label="Toggle menu"
                    aria-expanded={mobileOpen}
                    onClick={() => setMobileOpen((v) => !v)}
                >
                    {mobileOpen ? <IoCloseOutline /> : <RxHamburgerMenu />}
                </button>

                <nav className={`admin-header-nav${mobileOpen ? ' open' : ''}`}>
                    {!isAuthenticated && (
                        <Link className="admin-header-link" to="/login" onClick={() => setMobileOpen(false)}>
                            Login / Signup
                        </Link>
                    )}
                    {isAuthenticated && (
                        <div className="admin-header-user" ref={dropdownRef}>
                            <button
                                type="button"
                                className="admin-header-user-btn"
                                aria-expanded={isOpen}
                                onClick={() => setIsOpen((v) => !v)}
                            >
                                <span className="admin-header-user-name">
                                    {userData?.userDisplayName?.replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                                <span className="admin-header-user-role">
                                    {' ('}{toTitleCase(userData?.roleName)}
                                    {role === 'User' && userData?.hasFullAccess === true ? ' - Full Access' : ''}
                                    {')'}
                                </span>
                                <span className="admin-header-caret" aria-hidden="true">▾</span>
                            </button>
                            {isOpen && (
                                <ul className="admin-header-menu">
                                    {role !== 'User' && (
                                        <li><Link to="/tags-directory" onClick={() => setIsOpen(false)}>Tags Directory</Link></li>
                                    )}
                                    {role === 'Super_admin' && (
                                        <>
                                            <li><Link to="/superAdmin-list" onClick={() => setIsOpen(false)}>Super Admin List</Link></li>
                                            <li><Link to="/approver-list" onClick={() => setIsOpen(false)}>Approver List</Link></li>
                                            <li><Link to="/admin-list" onClick={() => setIsOpen(false)}>Admin List</Link></li>
                                            <li><Link to="/user-list" onClick={() => setIsOpen(false)}>User List</Link></li>
                                        </>
                                    )}
                                    {role !== 'User' && (
                                        <li><span className="as-link" onClick={goMediaList}>Media List</span></li>
                                    )}
                                    {(role === 'Super_admin' || role === 'APPROVER') && (
                                        <li><Link to="/media-requests" onClick={() => setIsOpen(false)}>Requests</Link></li>
                                    )}
                                    {role === 'User' && (
                                        <li><Link to="/my-requests" onClick={() => setIsOpen(false)}>My Requests</Link></li>
                                    )}
                                    <li><span className="as-link" onClick={logout}>Log out</span></li>
                                </ul>
                            )}
                        </div>
                    )}
                </nav>
            </div>
        </header>
    );
}

export default AdminHeader;
